import { useState } from "react";
import { Link } from "react-router";
import type { Lesson } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { useViewer } from "../../lib/session.tsx";
import { RowDisclosure } from "../../lib/RowDisclosure.tsx";
import { EntryChat } from "../schedule/EntryChat.tsx";
import { MessagesIcon } from "../shell/icons.tsx";
import type { OccurrenceChat } from "../occurrence-chat/use-occurrence-chat.ts";
import { CHAT_LABEL } from "../occurrence-chat/chat-text.ts";
import { ExternalCallLink } from "../schedule/ExternalCall.tsx";
import { CopyLink } from "../shell/CopyLink.tsx";
import { useGuestLinkCopy } from "../calls/call-link.ts";
import { rowActionsOf, rowSubOf } from "../calls/call-row.ts";
import { LessonWho } from "./LessonWho.tsx";
import { lessonWhenLabel } from "./lesson-when.ts";
import { hasPlanTools, PlanCell, PlanTools, usePlanLink } from "./PlanLink.tsx";

/**
 * Строка списка уроков: дата, урок, ученик, заготовка, состояние, действия.
 *
 * В строке ровно одно главное действие («Войти» у незавершённого, «Архив» у
 * завершённого со снимком). Редкое и необратимое уехало в развёртку строки:
 * пятая кнопка в ряду весит столько же, сколько первая, и «В корзину» рядом с
 * «Войти» уравнивает вход в урок с его удалением.
 *
 * ⚠️ **Ниже порога колонок семь слотов превращаются в две строки** (Р3
 * `docs/phone-design-decisions.md`, включено `data-rows="panel"` в
 * `LessonsPanel.tsx`):
 *
 *     [>] Название урока                         дата
 *         ученик                        [Войти] [чат]
 *
 * Состав разметки при этом НЕ меняется и порядок слотов тоже: колонку и строку
 * каждому слоту назначает CSS явно, а что спрятано и почему, сказано у самого
 * слота ниже. Ветки по ширине в этом файле нет ни одной и быть не должно:
 * второй источник правды о раскладке первым делом разойдётся с первым.
 */
export function LessonRow(props: {
  lesson: Lesson;
  /** Взят один раз на отрисовку списка: 200 вызовов `Date.now()` тут не нужны. */
  now: number;
  busy: boolean;
  run: (fn: () => Promise<void>) => void;
  /**
   * Чат занятия, либо `null`, если открыт чат другой строки.
   *
   * ⚠️ **Лента и черновик живут В ПАНЕЛИ, а не здесь, и открыт всегда ровно один
   * чат** (`docs/room-chat-design.md`, раздел 12). Свой хук на строку означал бы
   * двести таймеров и двести запросов в списке из двухсот уроков; свой черновик
   * на строку исчезал бы вместе со свёрнутой строкой.
   */
  chat: OccurrenceChat | null;
  onToggleChat: () => void;
}) {
  const l = props.lesson;
  const ended = l.status === "ended";
  const teacher = l.myRole === "teacher";
  /*
    Подпись и набор действий строки решает `calls/call-row.ts`: у звонка с
    преподавателями они свои, а отличить гостя звонка от ученика по одной роли
    нельзя (у обоих `student`). Архив открываем только когда снимок доски уже
    есть: ссылка на урок без снимка вела бы на экран «архив готовится», который
    для урока, где никто ничего не нарисовал, не закончится никогда
  */
  /** Просмотр кабинета админом: «Ссылки» в нём нет, выдача ключа это запись. */
  const viewing = useViewer() !== null;
  const acts = rowActionsOf(l, { viewing });
  const sub = rowSubOf(l);
  const archive = acts.archive;
  const plan = usePlanLink(l);
  const link = useGuestLinkCopy(l.id);
  /** Поле с адресом или отказ выдачи открыты под строкой. */
  const linkShown = link.state.phase === "manual" || link.state.phase === "failed";
  const [open, setOpen] = useState(false);
  /**
   * Подтверждение своё, а не `confirm()`: нативный диалог блокирует поток
   * страницы целиком, и на открытом уроке в соседней вкладке это заметно.
   */
  const [asking, setAsking] = useState(false);

  const when = lessonWhenLabel(l, props.now);
  const hasMore = acts.hasMore;

  return (
    <li className="row" data-dim={ended}>
      {/*
        Шеврон первым столбцом, а не кнопка «…» в ячейке действий: раскрытие
        детали строки это одна механика на всё приложение, см. шапку
        `lib/RowDisclosure.tsx`. Столбец занят пустым слотом и у строки без
        развёртки, иначе даты соседних строк разъезжались бы по горизонтали.

        ⚠️ На узкой строке пустой слот не держит ничего: там ведущая дорожка
        задана шириной значка, и имена выравнивает она, а не он. Слот туда
        просто встаёт сам (первая свободная клетка это и есть клетка шеврона) и
        не мешает; убирать его ради узкой раскладки нельзя, на широкой он
        по-прежнему держит колонку.
      */}
      {hasMore ? (
        <RowDisclosure
          open={open}
          action="Ещё действия"
          subject={l.title}
          onToggle={() => {
            setOpen(!open);
            // Закрытая развёртка не должна помнить наполовину заданный вопрос
            setAsking(false);
          }}
        />
      ) : (
        <span />
      )}

      {/*
        ⚠️ `data-narrow="keep"` это тот единственный слот, который на узкой
        строке остаётся при имени. Дата тут не украшение: за ней в этот список и
        приходят наравне с названием («когда этот урок»), и она же ключ порядка,
        по которому строки разложены по группам. Спрятав её, мы оставили бы
        список, порядок которого не объяснён ничем видимым.
      */}
      <span
        className="cell row__when"
        data-label="Дата"
        data-narrow="keep"
        data-tone={when.tone}
        title={when.title}
      >
        {when.text}
      </span>

      <span className="cell row__name">
        {l.title}
        {sub !== null && <span className="row__sub">{sub}</span>}
      </span>

      {/* Колонка общая с архивом: у занятия группы в ней стоит название группы.
          На узкой строке она становится подписью под именем: метку `data-narrow`
          ставит сама колонка, см. шапку `LessonWho.tsx` */}
      <LessonWho lesson={l} />

      {/*
        ⚠️ На узкой строке заготовка прячется, и прячет её общее правило по
        `data-label` (метки «оставить» у неё нет). Довод Р3 дословный: **прячем
        то, у чего есть второй адрес, и называем этот адрес.** Адресов у
        заготовки даже два, экран самой заготовки и её список «Уроки по этой
        заготовке», и оба ведут к тому же, к чему ведёт эта ячейка.
      */}
      <PlanCell plan={plan} linkable={teacher} />

      {/*
        ⚠️ Состояние на узкой строке тоже прячется, но довод у него ДРУГОЙ, и
        путать их нельзя. Второго адреса у состояния нет; оно прячется потому,
        что повторяет слово в слово соседний элемент: строка стоит под полосой
        своей группы, а полоса эта и называется «Идут», «Созданные»,
        «Завершённые». Пилюля «создан» под заголовком «Созданные» это не
        величина, это эхо. На широкой раскладке пилюля остаётся: там у неё своя
        колонка и своя шапка, и повтор стоит ширины колонки, а не строки экрана.
      */}
      <span className="cell" data-label="Состояние">
        {/* Тон только у идущего урока: он единственный требует действия сейчас */}
        <span className="pill" data-tone={l.status === "live" ? "ok" : undefined}>
          {ended ? "завершён" : l.status === "live" ? "идёт" : "создан"}
        </span>
      </span>

      <span className="cell row__act">
        {/*
          ⚠️ **Ниже порога колонок эта ячейка ОСТАЁТСЯ**, и ради этого списку
          заведено второе значение `data-rows` (решение владельца 17.09.2026).
          Общее правило Р3 прячет действия доводом «строка сама ведёт туда, где
          они живут», но имя урока не ссылка: вести отсюда некуда. Внутри вход в
          урок, то есть то, ради чего строку и открывают, и внешний созвон,
          который поражать в правах запрещено (раздел 2 CLAUDE.md). Спрятанная
          ячейка оставила бы на телефоне строку, из которой нельзя попасть на
          урок вообще.
        */}
        {/*
          ⚠️ Ячейка действий этой строки НЕ переведена на `btn--sm`, в отличие от
          остальных списков. Причина в том, что половина её содержимого приезжает
          общими компонентами (`ExternalCallLink` со своим значком копирования,
          и тот же значок стоит рядом с полем ссылки в редакторе серии, где он
          обязан быть по росту полю). Двадцать шесть пикселей рядом с тридцатью
          двумя в ОДНОЙ ячейке хуже, чем тридцать два во всей строке.
        */}
        {/*
          Внешний созвон рядом с входом в нашу комнату, а не вместо него и не
          мельче: раздел 7 docs/switching-costs.md. У завершённого урока его
          нет, там идти уже некуда.

          ⚠️ Ссылка приезжает УЖЕ РАЗРЕШЁННОЙ (своя урока, иначе серии): правило
          живёт на сервере в одном месте, и второго его экземпляра здесь нет.

          ⚠️ Вместе со ссылкой сюда приезжает и значок копирования: он живёт
          внутри `ExternalCallLink` и появился здесь заодно с карточкой занятия
          03.09.2026. Это проверено и оставлено решением, а не случайностью
          (разбор в шапке `features/schedule/ExternalCall.tsx`): адрес созвона
          пересылают родителю, ячейка действий уже несёт четыре элемента, и
          пятое СЛОВО ушло бы переносом, а фигура нет. Высоту строки на ощупь
          задаёт соседний «Войти», их растит одно и то же правило крупной цели

          ⚠️ **Всё это про ШИРОКУЮ раскладку.** Ниже порога колонок и домен, и
          значок копирования спрятаны правилом в `styles.css`: впятером ряд не
          сходится ни при какой ширине телефона. Разметка при этом не меняется,
          прячет их CSS, и на широкой оба на месте.
        */}
        {!ended && <ExternalCallLink url={l.externalCallUrl} />}
        {!ended && (
          <Link className="btn" to={`/lesson/${l.id}`}>
            Войти
          </Link>
        )}
        {ended && archive && (
          <Link className="btn" to={`/archive/${l.id}`}>
            Архив
          </Link>
        )}
        {/*
          Ссылка для преподавателей у звонка (9.2 `docs/teacher-calls-design.md`):
          звонок заводят заранее, и ссылку берут, не заходя в комнату. Нажатие
          спрашивает ссылку и кладёт её в буфер обмена; отказал буфер, и под
          строкой открывается поле с адресом, а эта же кнопка его закрывает.
          После отказа выдачи кнопка повторяет запрос: закрывать там нечего,
          кроме текста отказа
        */}
        {acts.guestLink && (
          <button
            type="button"
            className="btn"
            disabled={link.state.phase === "busy"}
            /* Раскрыто ровно тогда, когда под строкой показано поле с адресом;
               имя с названием звонка, как «Новая ссылка для ученика …» в
               списке учеников: кнопок «Ссылка» в списке бывает несколько */
            aria-expanded={link.state.phase === "manual"}
            aria-label={
              link.state.phase === "done" ? "Скопировано" : `Ссылка на звонок ${l.title}`
            }
            onClick={link.state.phase === "manual" ? link.close : link.copy}
          >
            {link.state.phase === "done" ? "Скопировано" : "Ссылка"}
          </button>
        )}
        {/*
          Чат отдельной кнопкой, а не пунктом развёртки «…», и это про частоту:
          развёртка держит редкое и необратимое («В корзину», «Завершить»), а
          переписку по занятию открывают тем же движением, что и сам урок.
          Значок, а не слово: в ячейке действий уже стоят «Войти» и «…», и пятое
          СЛОВО ушло бы переносом, а фигура нет (тот же довод, что у копирования
          ссылки на созвон)
        */}
        <button
          type="button"
          className="btn btn--ico btn--quiet"
          aria-label={CHAT_LABEL}
          title={CHAT_LABEL}
          aria-expanded={props.chat !== null}
          onClick={props.onToggleChat}
        >
          <MessagesIcon />
        </button>
      </span>

      {/*
        Чат своей строкой развёртки, а не внутри `row__more`: развёртку человек
        открывает ради корзины и заготовки, а чат ради переписки, и связывать их
        одним нажатием значило бы показывать корзину тому, кто пришёл читать.
        Полосу занимает целиком (`grid-column: 1 / -1` в стилях): в колонке
        шириной 7rem лента не живёт
      */}
      {props.chat !== null && (
        <div className="row__chat">
          <EntryChat
            chat={props.chat}
            role={teacher ? "teacher" : "student"}
            call={l.call !== undefined}
          />
        </div>
      )}

      {/*
        Исход «Ссылки» своей полосой, без шеврона, как одноразовая ссылка в
        списке учеников: раскрывать тут нечего, пока на кнопку не нажали.
        Адрес живёт в состоянии хука, пока полоса открыта, и больше нигде
      */}
      {acts.guestLink && linkShown && (
        <div className="row__more">
          {link.state.phase === "manual" ? (
            <CopyLink url={link.state.url} />
          ) : link.state.phase === "failed" ? (
            <p className="err" role="alert">
              {link.state.text}
            </p>
          ) : null}
        </div>
      )}

      {open && (
        <div className="row__more">
          {teacher && hasPlanTools(plan) && <PlanTools plan={plan} />}

          {/* У звонка шаг подтверждения говорит, что будет: конец для всех и
              стирание через срок корзины (9.2 `docs/teacher-calls-design.md`) */}
          {teacher && asking && acts.trashConfirm !== null && (
            <p className="muted">{acts.trashConfirm}</p>
          )}

          <div className="acts">
            {/* Снимок у незавершённого урока уже бывает: его снимают раз в пять
                минут и на выходе последнего участника */}
            {!ended && archive && (
              <Link className="btn" to={`/archive/${l.id}`}>
                Архив
              </Link>
            )}
            {acts.end && (
              <button
                className="btn"
                disabled={props.busy}
                onClick={() => props.run(async () => void (await api.endLesson(l.id)))}
              >
                Завершить
              </button>
            )}
            {/*
              Два начертания одного веса: обводка, пока удаление только
              предложено, и заливка на шаге подтверждения, когда следующее
              нажатие уже уносит урок. До этого «Точно в корзину» и «Отмена»
              были одинаковыми прямоугольниками
            */}
            {teacher &&
              (asking ? (
                <>
                  <button
                    className="btn btn--danger is-confirm"
                    disabled={props.busy}
                    onClick={() => props.run(async () => void (await api.trashLesson(l.id)))}
                  >
                    Точно в корзину
                  </button>
                  <button
                    className="btn btn--quiet"
                    disabled={props.busy}
                    onClick={() => setAsking(false)}
                  >
                    Отмена
                  </button>
                </>
              ) : (
                <button
                  className="btn btn--danger"
                  disabled={props.busy}
                  onClick={() => setAsking(true)}
                >
                  В корзину
                </button>
              ))}
          </div>
        </div>
      )}
    </li>
  );
}
